<template>
  <div>
    <div
      :class="[
        { 'px-0': isLoading },
        'add-to-schedule-button v-toolbar__button'
      ]"
      data-cy="add-to-schedule-button"
      @click="openAddToScheduleModal"
    >
      <template v-if="isLoading">
        <v-loader />
      </template>
      <v-icon v-else :icon="hasScheduledItems ? 'event_available' : 'event'" />
    </div>
    <v-modal short @close="resetState">
      <template :slot="addToSchedulePanelHash">
        <transition name="fade-out-left" mode="out-in">
          <action-header v-if="!alreadyAddedItem" :key="headingModal">
            {{ headingModal }}
          </action-header>
        </transition>
        <action-body>
          <transition name="fade-out-left" mode="out-in">
            <div
              v-if="isLoading"
              class="w-100 d-flex justify-content-center pt-5"
            >
              <v-loader />
            </div>
            <icon-message
              v-else-if="alreadyAddedItem"
              icon="done_all"
              :heading="$t('components.toolbar.addToScheduleButton.addScheduleHeading')"
              :description="
                $t('components.toolbar.addToScheduleButton.addScheduleDescription', {
                  pageContentType: pageContentType
                })
              "
            />
            <div
              v-else-if="showAddToSchedule"
              class="px-2 pt-1 pb-2"
              key="schedule"
            >
              <schedule-input
                :date="scheduldingDate"
                :heading="
                  $t('components.toolbar.addToScheduleButton.showAddToScheduleHeading', {
                    pageContentType: pageContentType
                  })
                "
                @input="value => (scheduldingDate = value)"
              />
              <div class="d-flex py-3 align-items-center schedule-notification">
                <v-button
                  round
                  x-small
                  secondary
                  disabled
                  class="mr-1 p-2 schedule-notification__button"
                >
                  <v-icon
                    :icon="
                      isAllRemindersInactive
                        ? 'notifications_off'
                        : 'notifications_active'
                    "
                    size="0.75rem"
                    class="m-0 p-0"
                  />
                </v-button>
                <div class="schedule-notification__text">
                  <span v-html="notificationsInfo" />
                </div>
              </div>
            </div>

            <div v-else key="scheduled-items" class="scheduled-items">
              <div
                v-for="item in scheduledItems"
                :key="item.id"
                class="d-flex scheduled-items__row justify-content-between align-items-center p-2"
              >
                <div
                  @click="() => goToDateOnSchedule(item.starts_at)"
                  class="d-flex align-items-center flex-grow-1"
                >
                  <v-icon
                    icon="event_available"
                    size="1.8rem"
                    class="mr-1 text-grey-50"
                  />
                  <div class="scheduled-items__text text-grey-90">
                    {{ toDate(item.starts_at, "MMMM DD, YYYY") }}
                  </div>
                  <v-icon
                    icon="arrow_forward"
                    class="text-grey-60 ml-auto mr-2"
                    size="1.25rem"
                  />
                </div>
                <div class="d-flex align-items-center">
                  <v-button
                    v-if="canDelete(item)"
                    round
                    x-small
                    secondary
                    with-icon
                    class="m-0 p-1"
                    @click="() => (removingScheduledItem = item)"
                  >
                    <v-icon
                      icon="close"
                      class="text-grey-60 font-weight-bold"
                      size="0.5rem"
                    />
                  </v-button>
                </div>
              </div>
            </div>
          </transition>
        </action-body>
        <action-footer classes="w-100 d-flex justify-content-between">
          <div v-if="isLoading" class="w-100 d-flex justify-content-center">
            <v-loader />
          </div>

          <template v-else-if="showAddToSchedule || alreadyAddedItem">
            <template v-if="alreadyAddedItem">
              <v-button
                class="flex-shrink-0"
                ghost
                @click="viewAllScheduledItems"
              >
                {{ $t("components.toolbar.addToScheduleButton.back") }}
              </v-button>
              <v-button block @click="handleViewShedule">{{
                $t("components.toolbar.addToScheduleButton.viewSchedule")
              }}</v-button>
            </template>
            <v-button
              v-else
              block
              :disabled="!scheduldingDate"
              :is-loading="isAdding"
              @click="addToSchedule"
            >
              {{ $t("components.toolbar.addToScheduleButton.addToSchedule") }}
            </v-button>
          </template>

          <v-button v-else block @click="() => (isSchedulingAgain = true)">
            {{ $t("components.toolbar.addToScheduleButton.scheduleAgainButton") }}
          </v-button>
        </action-footer>
      </template>
    </v-modal>

    <!-- TODO: Extract -->
    <alert-dialog
      :key="_uid"
      :title="$t('components.toolbar.addToScheduleButton.dialogRemoveDateTitle')"
      :actions="[
        {
          title: $t('components.toolbar.addToScheduleButton.dialogRemoveDateCancel'),
          callback: closeRemoveConfirm
        },
        {
          title: $t('components.toolbar.addToScheduleButton.dialogRemoveDateYes'),
          callback: confirmRemoveFromSchedule
        }
      ]"
      :show="!!removingScheduledItem"
      @close="closeRemoveConfirm"
    >
      {{ $t("components.toolbar.addToScheduleButton.dialogRemoveDateContent") }}
    </alert-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { startOf, toDate, isPast, isSameDay } from "@/lib/date-utils"
import { isBuilderContext } from "@/lib/utils"
import { SCOPE_TYPES } from "@/lib/schedule-utils"

import ActionHeader from "@/components/modals/shared/ActionHeader"
import ActionBody from "@/components/modals/shared/ActionBody"
import ActionFooter from "@/components/modals/shared/ActionFooter"
import IconMessage from "@/components/modals/shared/IconMessage"

export default {
  components: {
    ActionHeader,
    ActionBody,
    ActionFooter,
    IconMessage,
    ScheduleInput: () => import("@/components/schedule/ScheduleInput"),
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    itemId: { type: Number, required: true },
    itemType: { type: String, default: "Page" },
    pageContentType: { type: String, default: "page" }
  },

  data() {
    return {
      scheduldingDate: null,
      isAdding: false,
      alreadyAddedItem: null,
      isFetchingScheduledItems: false,
      removingScheduledItem: null,
      isSchedulingAgain: false,
      isDailyReminders: false,
      isWeeklyReminders: false
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters("schedule", ["getScheduledItemsFor"]),

    isAllRemindersInactive() {
      return !this.isDailyReminders && !this.isWeeklyReminders
    },

    headingModal() {
      return this.hasScheduledItems && !this.showAddToSchedule
        ? this.$t("components.toolbar.addToScheduleButton.upComingSchedule")
        : this.$t("components.toolbar.addToScheduleButton.addMySchedule")
    },

    hasScheduledItems() {
      return this.scheduledItems.length > 0
    },

    showAddToSchedule() {
      return this.isSchedulingAgain || !this.hasScheduledItems
    },

    notificationsInfo() {
      if (this.isDailyReminders && this.isWeeklyReminders) {
        return this.$t("components.toolbar.addToScheduleButton.remindedOnDayAndStartWeek")
      } else if (this.isDailyReminders) {
        return this.$t("components.toolbar.addToScheduleButton.remindedOnDay")
      } else if (this.isWeeklyReminders) {
        return this.$t("components.toolbar.addToScheduleButton.remindedOnStartWeek")
      } else {
        return this.$t("components.toolbar.addToScheduleButton.yourNotificationReminderDisabled")
      }
    },

    scheduledItems() {
      return !isBuilderContext()
        ? this.getScheduledItemsFor(this.itemId, this.itemType)
            .filter(item => item.scope_type === SCOPE_TYPES.SUBSCRIBER)
            .sort(
              (a, b) =>
                new Date(a.starts_at).getTime() -
                new Date(b.starts_at).getTime()
            )
        : []
    },

    isLoading() {
      return this.isFetchingScheduledItems
    },

    addToSchedulePanelHash() {
      return `#schedule`
    }
  },

  mounted() {
    if (this.isAuthenticated) this.fetchItems()
  },

  watch: {
    isAuthenticated() {
      if (this.isAuthenticated) {
        this.fetchItems()
      }
    }
  },

  beforeDestroy() {
    this.resetState()
  },

  methods: {
    toDate,

    ...mapActions("schedule", [
      "fetchScheduledItems",
      "createScheduledItem",
      "removeScheduledItem"
    ]),

    fetchItems() {
      if (isBuilderContext()) return

      this.isFetchingScheduledItems = true
      this.fetchScheduledItems({
        itemId: this.itemId,
        itemType: this.itemType,
        from: startOf("date")
      })
        .then(({ is_daily_reminders, is_weekly_reminders }) => {
          this.isDailyReminders = is_daily_reminders
          this.isWeeklyReminders = is_weekly_reminders
        })
        .finally(() => {
          this.isFetchingScheduledItems = false
        })
    },

    isPastDate(date) {
      return isPast(date) && !isSameDay(date)
    },

    openAddToScheduleModal() {
      if (!this.isAuthenticated) {
        this.$router.push("#auth")

        return false
      }

      this.$router.push(this.addToSchedulePanelHash)
    },

    addToSchedule() {
      if (!this.isAuthenticated) {
        this.$router.push("#auth")
        return
      }

      if (this.isPastDate(this.scheduldingDate)) {
        this.$notify(this.$t("components.toolbar.addToScheduleButton.notifyNotSchedule"))
        return
      }

      this.isAdding = true
      this.createScheduledItem({
        itemId: this.itemId,
        itemType: this.itemType,
        date: this.scheduldingDate
      })
        .then(item => {
          this.alreadyAddedItem = item
        })
        .finally(() => {
          this.isAdding = false
        })
    },

    confirmRemoveFromSchedule() {
      this.removeScheduledItem(this.removingScheduledItem.id).finally(() => {
        this.resetState()
        this.$notify(
          this.$t("components.toolbar.addToScheduleButton.notifyRemoveSchedule")
        )
      })
    },

    closeRemoveConfirm() {
      this.removingScheduledItem = null
    },

    handleCancel() {
      this.$router.removeHash()
      this.resetState()
    },

    goToDateOnSchedule(date) {
      this.$router.push({
        path: "/me/schedule",
        query: { date: toDate(date, "YYYY-MM-DD") }
      })
    },

    handleViewShedule() {
      this.goToDateOnSchedule(this.alreadyAddedItem.starts_at)
      this.resetState()
    },

    resetState() {
      this.removingScheduledItem = null
      this.alreadyAddedItem = null
      this.isSchedulingAgain = false
      this.scheduldingDate = null
    },

    viewAllScheduledItems() {
      this.isSchedulingAgain = false
      this.alreadyAddedItem = null
    },

    canDelete(item) {
      return item.scope_type === SCOPE_TYPES.SUBSCRIBER
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
.action-panel__action {
  .scheduled-items {
    &__text {
      font-size: 18px;
      font-weight: bold;
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.schedule-notification {
  &__text {
    font-size: $font-size--x-small;
    color: $color-ui--grey-60;

    b {
      color: $color-ui--dark-blue;
    }
  }
}
</style>
